import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { MultiplicationMasterAPIStat } from '~/redux/apis/user-stats-api'
import GameStatsText from '~/statistics-v2/components/gameStatsText'

export default function StatsMultiplicationMaster({
  stats,
}: {
  stats?: MultiplicationMasterAPIStat
}): React.ReactElement {
  const { t } = useTranslation('statisticsV2')

  const sprintHighscore = stats?.details.sprint?.score || 0
  const survivalHighscore = stats?.details.survival?.score || 0

  /*
   * Tabellmesteren rounds score too, and they score high — a player who only plays times tables used
   * to see 0 here because the card read Sprint and Survival alone. Their scores live per table under
   * `details.tablesMaster.tables`, so the best of them has to be folded in.
   *
   * The label names the mode the score came from, at the same granularity as the other two rows: the
   * mode, not which table within it.
   */
  const bestTableHighscore = Math.max(
    0,
    ...Object.values(stats?.details.tablesMaster?.tables ?? {}).map((table) => table.score || 0),
  )
  const bestNamedHighscore = Math.max(sprintHighscore, survivalHighscore)
  const isTableBest = bestTableHighscore > bestNamedHighscore
  const highscore = isTableBest ? bestTableHighscore : bestNamedHighscore

  const highscoreLabel = isTableBest
    ? t('games.highscoreTable')
    : sprintHighscore >= survivalHighscore
    ? t('games.highscoreSprint')
    : t('games.highscoreSurvival')

  return (
    <div className="flex flex-1 flex-col gap-2 p-4 bg-purple-midnight rounded border border-purple min-w-50">
      <div className="flex gap-2 items-center h-12">
        <ResponsiveImage height={40} srcKey="gamification/games/multi-master.png" width={40} />
        <h3 className="text-white text-sm font-bold">{t(`games.multiplicationMaster`)}</h3>
      </div>
      <span className="border-b border-purple" />
      <div className="flex flex-col gap-2 py-2">
        <GameStatsText text={highscoreLabel} value={highscore} />
        <GameStatsText text={t('games.activities')} value={stats?.summary.totalActivitiesSolvedCount || 0} />
        <GameStatsText text={t('games.accuracy')} value={`${stats?.summary.accuracy || 0}%`} />
      </div>
    </div>
  )
}
